import React, { useMemo, useContext } from 'react';
import { Box } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Line,
  ComposedChart,
} from 'recharts';
import { format, isSameHour, differenceInDays } from 'date-fns';
import './priceChart.scss';
import Tooltip from '../../../../../atomicDesign/molecules/ChartToolTip';
import AdminContext from '../../../../../context/admin';
import {
  convertCurrency,
  formatPrice,
  getDecimalPlaces,
} from '../../../../../utils/currencyConversion';
import MuiFaIcon from '../../../../../atomicDesign/atoms/MuiFaIcon';
export default function PriceChart({
  data,
  height,
  hideCurrentPrice = false,
  dateFormater,
  currency = 'EUR',
  exchangeRates = null,
}) {
  const { language } = useContext(AdminContext);
  let flatData = [];
  const priceBlue = '#3b82f6';
  let rightNow = new Date();
  let twoHoursAgo = new Date(rightNow - 1000 * 60 * 60 * 2);
  const decimals = getDecimalPlaces(currency);

  if (data?.length > 0) {
    for (let day of data) {
      for (let hour of day?.data || []) {
        let thisDateTime = new Date(hour.time_start);
        if (thisDateTime < twoHoursAgo) continue;

        const convertedPrice = convertCurrency(
          hour.EUR_per_kWh,
          currency,
          exchangeRates
        );
        flatData.push({
          time_start: hour.time_start,
          EUR_per_kWh: hour.EUR_per_kWh,
          price_per_kWh: convertedPrice,
        });
      }
    }
  }

  const totalDays = useMemo(() => {
    if (!flatData || flatData.length === 0) return 0;
    const firstReading = flatData[0];
    const lastReading = flatData[flatData.length - 1];
    let firstDate = new Date(firstReading?.date ?? firstReading?.timestamp);
    let lastDate = new Date(lastReading?.date ?? lastReading?.timestamp);
    const days = differenceInDays(lastDate, firstDate);
    return days;
  }, [flatData]);

  const xAxisTickFormatter = (tickValue) => {
    const dateObj = new Date(tickValue);
    if (totalDays > 7) {
      return format(dateObj, 'MMM dd');
    } else if (totalDays > 2) {
      return format(dateObj, 'MMM dd, HH:mm'); // e.g. 'Mar 12, 14:00'
    } else {
      return format(dateObj, 'HH:mm');
    }
  };

  // Find the current hour's data for the ReferenceLine and overlay
  const currentHourData = flatData.find((entry) =>
    isSameHour(new Date(entry.time_start), new Date())
  );

  const currentHourTimestamp = currentHourData?.time_start;

  const prices = flatData.map((item) => item.price_per_kWh);
  const avgPrice =
    prices.length > 0
      ? prices.reduce((sum, price) => sum + price, 0) / prices.length
      : 0;
  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
  const currentPrice =
    currentHourData?.price_per_kWh || (prices.length > 0 ? prices[0] : 0);

  const markerDotStyle = {
    r: 5,
    fill: priceBlue,
    stroke: '#ffffff',
    strokeWidth: 2,
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  };

  const processedData = flatData.map((item) => ({
    ...item,
    current_marker:
      item.time_start === currentHourTimestamp ? item.price_per_kWh : null,
  }));

  // determine price trend
  const priceDirection = currentPrice > avgPrice ? 'up' : 'down';

  const CustomPriceTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const dataPoint = payload[0].payload;

    let priceCategory = language?.eosDashboard?.normal ?? 'Normal';
    let priceColor = '#3b82f6';

    if (dataPoint.price_per_kWh > avgPrice * 1.01) {
      priceCategory = language?.eosDashboard?.higher ?? 'Higher';
      priceColor = '#dc2626';
    } else if (dataPoint.price_per_kWh < avgPrice * 0.99) {
      priceCategory = language?.eosDashboard?.lower ?? 'Lower';
      priceColor = '#16a34a';
    }

    const priceDisplay = `${formatPrice(
      dataPoint.price_per_kWh,
      currency,
      decimals
    )}/kWh`;

    return (
      <Tooltip>
        <Tooltip.Time>{format(new Date(label), 'MMM dd, HH:mm')}</Tooltip.Time>

        <Tooltip.Section>
          <span
            className="value"
            style={{ color: priceColor, fontSize: '1.2rem', fontWeight: 600 }}
          >
            {priceDisplay}
          </span>
          <br />
          <span className="category" style={{ color: priceColor }}>
            {priceCategory} {language?.eosDashboard?.priceWord ?? 'Price'}
          </span>
        </Tooltip.Section>

        <Tooltip.Section>
          <Tooltip.Row
            label={language?.eosDashboard?.vsAverage ?? 'Vs Average'}
            value={`${(
              ((dataPoint.price_per_kWh - avgPrice) / avgPrice) *
              100
            ).toFixed(1)}%`}
            markColor={priceColor}
          />
        </Tooltip.Section>
      </Tooltip>
    );
  };

  const yMin = prices.length > 0 ? Math.min(...prices) - 0.1 : -2;
  const yMax = prices.length > 0 ? Math.max(...prices) + 0.1 : 10;

  return (
    <div className="price-chart-container">
      <div className="chart-header">
        <div className="chart-title-area">
          <div className="chart-title">
            <MuiFaIcon name="fas fa-bolt" className="chart-icon" />
            {language?.eosDashboard?.electricityPrice ?? 'Electricity Price'}
          </div>
          <p className="chart-subtitle">
            {language?.eosDashboard?.weatherTemperature?.headerSubtitle ??
              'Forecast'}
          </p>
        </div>

        <div className="chart-right-section">
          <div className="price-stats-box">
            <div className="stat-item">
              <div className="stat-label">Min</div>
              <div className="stat-value">
                {formatPrice(minPrice, currency, decimals)}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Avg</div>
              <div className="stat-value">
                {formatPrice(avgPrice, currency, decimals)}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Max</div>
              <div className="stat-value">
                {formatPrice(maxPrice, currency, decimals)}
              </div>
            </div>
            <div className="stat-item current">
              <div className="stat-label">
                {' '}
                {language?.eosDashboard?.weatherTemperature?.stats?.current ??
                  'Current'}
              </div>
              <div className={`stat-value ${priceDirection}`}>
                {formatPrice(currentPrice, currency, decimals)}
                <span className="price-trend">
                  {priceDirection === 'up' ? '↑' : '↓'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="chart-content" style={{ height: height ?? 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={processedData}
            margin={{
              top: 10,
              right: 5,
              left: 25,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id="priceGradientBar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={priceBlue} stopOpacity={0.2} />
                <stop offset="100%" stopColor={priceBlue} stopOpacity={0.02} />
              </linearGradient>
            </defs>

            <CartesianGrid
              stroke="#f1f5f9"
              strokeDasharray="0"
              vertical={false}
              horizontal={true}
              opacity={0.7}
            />
            <XAxis
              dataKey="time_start"
              tickFormatter={xAxisTickFormatter}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
              tickMargin={8}
            />
            <YAxis
              domain={[yMin, yMax]}
              axisLine={false}
              tickLine={false}
              tickFormatter={(tick) => formatPrice(tick, currency, decimals)}
              tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
              width={60}
              tickMargin={8}
            />

            <RechartsTooltip content={<CustomPriceTooltip />} />

            {currentHourTimestamp && (
              <ReferenceLine
                x={currentHourTimestamp}
                stroke="#64748b"
                strokeWidth={1}
                opacity={0.6}
              />
            )}

            <ReferenceLine
              y={avgPrice}
              stroke="#94a3b8"
              strokeDasharray="2 2"
              opacity={0.7}
              label={{
                value: 'Avg',
                position: 'insideTopRight',
                fill: '#64748b',
                fontSize: 11,
                fontWeight: 500,
              }}
            />

            <Area
              type="monotone"
              dataKey="price_per_kWh"
              stroke={priceBlue}
              strokeWidth={2}
              fill="url(#priceGradientBar)"
              dot={false}
              connectNulls
              activeDot={{
                r: 4,
                fill: priceBlue,
                stroke: '#ffffff',
                strokeWidth: 2,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
              }}
            />

            <Line
              type="monotone"
              dataKey="current_marker"
              stroke="none"
              dot={markerDotStyle}
              connectNulls
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

PriceChart.getStats = (data, currency = 'EUR', exchangeRates = null) => {
  if (!data || !data.length) return { min: 0, avg: 0, max: 0 };

  let flatData = [];
  let rightNow = new Date();
  let twoHoursAgo = new Date(rightNow - 1000 * 60 * 60 * 2);

  if (data?.length > 0) {
    for (let day of data) {
      if (day?.data?.length > 0) {
        for (let hour of day.data) {
          let thisDateTime = new Date(hour.time_start);
          if (thisDateTime < twoHoursAgo) continue;

          const convertedPrice = convertCurrency(
            hour.EUR_per_kWh,
            currency,
            exchangeRates
          );
          flatData.push({
            time_start: hour.time_start,
            price_per_kWh: convertedPrice,
          });
        }
      }
    }
  }

  const prices = flatData.map((item) => item.price_per_kWh);
  return {
    min: prices.length > 0 ? Math.min(...prices) : 0,
    avg:
      prices.length > 0
        ? prices.reduce((sum, price) => sum + price, 0) / prices.length
        : 0,
    max: prices.length > 0 ? Math.max(...prices) : 0,
  };
};
